// Design tokens -- per ui-design.md methodology
// Starting with Shadcn/Tailwind-compatible defaults, customized as needed

export const tokens = {
  colors: {
    primary: {
      50: "#eff6ff",
      100: "#dbeafe",
      200: "#bfdbfe",
      300: "#93c5fd",
      400: "#60a5fa",
      500: "#3b82f6",
      600: "#2563eb",
      700: "#1d4ed8",
      800: "#1e40af",
      900: "#1e3a8a",
      950: "#172554",
    },
    neutral: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#e5e5e5",
      300: "#d4d4d4",
      400: "#a3a3a3",
      500: "#737373",
      600: "#525252",
      700: "#404040",
      800: "#262626",
      900: "#171717",
      950: "#0a0a0a",
    },
    success: {
      50: "#f0fdf4",
      500: "#22c55e",
      700: "#15803d",
    },
    error: {
      50: "#fef2f2",
      500: "#ef4444",
      700: "#b91c1c",
    },
    warning: {
      50: "#fffbeb",
      500: "#f59e0b",
      700: "#b45309",
    },
    background: "#ffffff",
    foreground: "#0a0a0a",
    card: "#ffffff",
    muted: "#f5f5f5",
    mutedForeground: "#737373",
    border: "#e5e5e5",
    input: "#e5e5e5",
    ring: "#3b82f6",

    // Domain visualization palette -- 3D field viewer scene materials.
    // These are not UI chrome; they colour Three.js meshes (placement
    // status + asset category) and have no semantic-token equivalent.
    // Tokenized here so component code never hardcodes hex. See ui-design.md.
    visualization: {
      placementActive: "#22c55e",
      placementReserved: "#eab308",
      placementNone: "#94a3b8",
      categoryBarrier: "#6366f1",
      categoryDugout: "#8b5cf6",
      categoryPavilion: "#f59e0b",
      categoryJersey: "#ec4899",
      categoryOther: "#6b7280",
    },
  },

  spacing: {
    0: 0,
    0.5: 2,
    1: 4,
    1.5: 6,
    2: 8,
    2.5: 10,
    3: 12,
    3.5: 14,
    4: 16,
    5: 20,
    6: 24,
    8: 32,
    10: 40,
    12: 48,
    16: 64,
    20: 80,
    24: 96,
  },

  typography: {
    fontFamily: {
      sans: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      mono: 'ui-monospace, "Cascadia Code", "Source Code Pro", monospace',
    },
    fontSize: {
      xs: 12,
      sm: 14,
      base: 16,
      lg: 18,
      xl: 20,
      "2xl": 24,
      "3xl": 30,
      "4xl": 36,
    },
    fontWeight: {
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  borderRadius: {
    none: 0,
    sm: 4,
    md: 6,
    lg: 8,
    xl: 12,
    full: 9999,
  },

  borderWidth: {
    0: 0,
    1: 1,
    2: 2,
  },

  shadows: {
    none: "none",
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  },

  animation: {
    duration: {
      fast: 150,
      normal: 200,
      slow: 300,
    },
  },

  breakpoints: {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
  },

  zIndex: {
    base: 0,
    dropdown: 50,
    overlay: 40,
    modal: 100,
    toast: 150,
  },
} as const;
